import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import { Ledger, StatusCell, What } from '../shared/cells';
import { FilterBar } from '../shared/controls';
import { fetch_needs_attention_categories } from '../api';
import { fmt_count } from '../format';
import { CATEGORIES_EMPTY_TEXT, CATEGORIES_LOAD_FAILED_TEXT, categories_view } from './adapters';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { category_csv_columns, na_csv_name } from './csv_columns';

/*
 * one tier split by issue category: counts, teachers, sessions and ₹ at stake per category. A row
 * opens that category's issues; "All issues" opens the whole tier as before.
 */
const CategoryTable = ({
  pay_month,
  tier,
  month_label,
  teacher_filter = null,
  on_select,
  on_view_all,
  on_back,
  on_error
}) => {
  const [loading, setLoading] = useState(true);
  const [load_failed, setLoadFailed] = useState(false);
  const [view, setView] = useState(categories_view(null));
  // Only the latest request may render: a slow answer for another tier must not land here.
  const seq = useRef(0);

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setLoading(true);
    setLoadFailed(false);
    fetch_needs_attention_categories(pay_month, tier)
      .then((data) => {
        if (mine !== seq.current) return;
        setView(categories_view(data));
        setLoading(false);
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        on_error(e, CATEGORIES_LOAD_FAILED_TEXT);
        setLoadFailed(true);
        setLoading(false);
      });
  }, [pay_month, tier, on_error]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (r) => on_select(r.issue_code, r.label);

  const plural = (n, word) => `${fmt_count(n)} ${n === 1 ? word : `${word}s`}`;
  const view_button = (r, className) => (
    <button
      type="button"
      className={className}
      aria-label={`View issues: ${r.label}`}
      onClick={() => open(r)}
    >
      View issues
    </button>
  );
  const blocks_pill = (r) =>
    r.blocks_close ? <StatusPill tone="red">Blocks close</StatusPill> : null;

  // List-row pattern: category · counts · ₹ at stake · status and action. Cards below 660px.
  const columns = [
    {
      key: 'label',
      role: 'identifier',
      label: 'Category',
      track: { compact: '32%', full: '32%' },
      render: (r) => (
        <What
          title={
            <button type="button" className="tp-link tp-na-tier-name" onClick={() => open(r)}>
              {r.label}
            </button>
          }
          lines={[
            `${plural(r.teachers, 'teacher')} · ${plural(r.sessions, 'session')}`,
            r.suggested_action
          ]}
        />
      )
    },
    {
      key: 'counts',
      label: 'Issues',
      render: (r) => (
        <dl className="tp-na-counts">
          {[
            ['Open', r.open_count],
            ['Acknowledged', r.acknowledged_count],
            ['Resolved', r.resolved_count]
          ].map(([k, v]) => (
            <div key={k} className="tp-na-count" data-count={k}>
              <dt>{k}</dt>
              <dd className={v ? undefined : 'tp-muted'}>{fmt_count(v)}</dd>
            </div>
          ))}
        </dl>
      )
    },
    {
      key: 'amount_at_stake',
      role: 'amount',
      label: '₹ at stake',
      align: 'right',
      track: { compact: '144px', full: '156px' },
      render: (r) => <span className="tp-c-amt">{r.amount_text}</span>,
      card_render: (r) => <Ledger total={{ label: 'At stake', value: r.amount_text }} />
    },
    {
      key: 'blocks_close',
      role: 'status',
      label: 'Status',
      align: 'right',
      track: { compact: '132px', full: '150px' },
      render: (r) => (
        <StatusCell
          chip={blocks_pill(r)}
          actions={<div className="tp-c-acts">{view_button(r, 'tp-btn-out tp-na-act')}</div>}
        />
      ),
      card_render: blocks_pill
    },
    {
      key: 'actions',
      role: 'actions',
      label: 'Actions',
      in_table: false,
      card_render: (r) => view_button(r, 'tp-btn-out')
    }
  ];

  return (
    <div>
      <FilterBar label="Category filters">
        <button type="button" className="tp-btn-out tp-na-back" onClick={on_back}>
          ← Back to tiers
        </button>
        {teacher_filter}
      </FilterBar>
      <div className="tp-card">
        <div className="tp-card-hd">
          <span className="tp-card-hd-main">
            <span className="tp-card-hd-title">
              {view.tier_label || tier} by category{month_label ? ` · ${month_label}` : ''}
            </span>
            {loading || load_failed ? null : (
              <span className="tp-card-hd-sub">
                {fmt_count(view.rows.length)} categories · {fmt_count(view.live_count)} with open
                work
              </span>
            )}
          </span>
          <span className="tp-card-hd-acts">
            <button type="button" className="tp-btn-out" onClick={on_view_all}>
              All issues in this tier
            </button>
            {loading || load_failed ? null : (
              <DownloadCsvButton
                columns={category_csv_columns}
                rows={view.rows}
                file_name={() =>
                  na_csv_name(`categories-${String(view.tier || tier).toLowerCase()}`, pay_month)
                }
              />
            )}
          </span>
        </div>
        {loading ? <CustomLoading /> : null}
        {!loading && load_failed ? (
          <LoadFailed text={CATEGORIES_LOAD_FAILED_TEXT} on_retry={load} />
        ) : null}
        {!loading && !load_failed ? (
          <DataTable
            columns={columns}
            rows={view.rows}
            rowKey={(r) => r.key}
            emptyText={CATEGORIES_EMPTY_TEXT}
            card_below={660}
            responsive
          />
        ) : null}
      </div>
    </div>
  );
};

export default CategoryTable;
